<template>
  <SfModalPortal
    modal-id="meeting-external-note-modal"
    size="m"
    :scrollable-content="true"
    @close="flushPastedNotesSave"
  >
    <template #title>{{ copy.title }}</template>

    <template #messages>
      <SfModalMessage v-if="error" variant="error">
        {{ error }}
      </SfModalMessage>
    </template>

    <template #body>
      <div class="tw-min-h-[260px] tw-flex tw-flex-col tw-gap-3">
        <div class="tw-text-sf-gray tw-text-md tw-whitespace-pre-line">
          {{ copy.subtitle }}
        </div>
        <div v-tooltip.placementBottom="atLimit ? copy.limitReached : ''">
          <div
            :class="{
              'tw-pointer-events-none tw-opacity-50': atLimit,
            }"
          >
            <DirectUploadDropzone
              :accept="allowedFilenameExtensions"
              :on-file-added="fileAdded"
              :on-file-invalid="fileInvalid"
              :on-file-progress="fileProgress"
              :on-file-error="fileError"
              :on-file-uploaded="fileUploaded"
              :multiple="true"
            >
              <UploadDropzone />
            </DirectUploadDropzone>
          </div>
        </div>
        <div
          v-if="documents.length || pendingUploads.length"
          class="tw-flex tw-flex-col tw-gap-2"
        >
          <DocumentCard
            v-for="note in documents"
            :key="note.unique_id"
            :document="note"
            @delete="handleDelete(note)"
          />
          <DocumentCard
            v-for="pending in pendingUploads"
            :key="pending.uuid"
            :document="pending.document"
            :progress="pending.progress"
          />
        </div>
        <label class="tw-flex tw-flex-col tw-gap-2 tw-text-sf-gray tw-text-md">
          {{ copy.pastedNotesLabel }}
          <textarea
            :value="pastedNotes"
            :placeholder="copy.pastedNotesPlaceholder"
            rows="6"
            class="tw-w-full tw-min-h-[120px]"
            @input="handlePastedNotesInput"
            @blur="flushPastedNotesSave"
          />
        </label>
      </div>
    </template>

    <template #footer>
      <SfButtonDs variant="secondary" @click="closeModal">
        {{ copy.closeLabel }}
      </SfButtonDs>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import { inject } from 'vue'
import { storeToRefs } from 'pinia'

import type {
  PreparePageData,
  ExternalNoteUploaderItem,
} from '@app/application/client_meetings/types'

import {
  SfModalMessage,
  SfModalPortal,
} from '@app/shared/vue/components/sf-modal'
import { SfButtonDs } from '@silverfin/ui-components'
import DirectUploadDropzone from '@app/shared/vue/components/direct-upload/direct-upload-dropzone.vue'
import UploadDropzone from '@app/application/client_meetings/screens/prepare/components/external-note-modal/upload-dropzone.vue'
import { useExternalNoteStore } from '@app/application/client_meetings/screens/prepare/stores/external-note'
import useI18n from '@app/shared/vue/composables/use-I18n'
import DocumentCard from '@app/application/client_meetings/screens/prepare/components/external-note-modal/document-card.vue'
import { allowedFilenameExtensions } from '@app/application/client_meetings/constants'

const I18n = useI18n()
const pageData = inject('pageData') as PreparePageData

const copy = {
  title: I18n.t('client_meetings.prepare.external_note_modal.title'),
  subtitle: I18n.t('client_meetings.prepare.external_note_modal.subtitle'),
  closeLabel: I18n.t('common.close'),
  limitReached: I18n.t(
    'client_meetings.prepare.external_note_modal.limit_reached',
    { count: pageData.maxExternalNotes }
  ),
  pastedNotesLabel: I18n.t(
    'client_meetings.prepare.external_note_modal.pasted_notes_label'
  ),
  pastedNotesPlaceholder: I18n.t(
    'client_meetings.prepare.external_note_modal.pasted_notes_placeholder'
  ),
}

const externalNoteStore = useExternalNoteStore()
const { documents, pendingUploads, error, atLimit, pastedNotes } =
  storeToRefs(externalNoteStore)
const {
  fileInvalid,
  fileAdded,
  fileProgress,
  fileError,
  fileUploaded,
  deleteExternalNote,
  setPastedNotes,
  flushPastedNotesSave,
} = externalNoteStore

const closeModal = () => {
  void flushPastedNotesSave()
  window.modalCitizen.closeModalPortal()
}

async function handleDelete(note: ExternalNoteUploaderItem) {
  await deleteExternalNote(note)
}

function handlePastedNotesInput(event: Event) {
  const target = event.target as HTMLTextAreaElement
  setPastedNotes(target.value)
}
</script>
